/**
 * WHAT:
 *   Custom hook for managing scan progress event listeners and UI timer.
 *
 * WHY:
 *   Encapsulates Tauri event listening logic for scan progress tracking.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Hooks
 *
 * DEPENDENCIES:
 *   - Tauri event API (listen)
 *   - React useEffect hook
 *   - TestTabState for state management
 *
 * RISKS:
 *   - WARN: Event listeners must be properly cleaned up on unmount
 *   - NOTE: Timer interval may continue if component unmounts during scan
 *   - NOTE: Multiple event listeners registered - ensure cleanup order
 */

import type { ScanProgressPayload, ScanDonePayload, TestTabState } from "../types";

import { listen } from "@tauri-apps/api/event";
import { useEffect } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";

/**
 * WHAT:
 *   Custom hook that sets up event listeners for scan progress events.
 *
 * WHY:
 *   Centralizes event listener setup and cleanup for scan progress tracking.
 *
 * INPUTS:
 *   - state: TestTabState — State object containing all state setters
 *
 * BEHAVIOR:
 *   - Listens to 'scan_progress' events for real-time progress updates
 *   - Listens to 'scan_progress_done' events for scan completion
 *   - Sets up UI timer for elapsed time calculation
 *   - Cleans up event listeners on unmount
 *
 * RISKS:
 *   - NOTE: Event listeners may not properly clean up on unmount if component unmounts during scan
 *
 * @param state
 *
 */
export const useScanProgress = (state: TestTabState): void => {
  const {
    isScanning,
    scanStartTs,
    setCurrentScanFile,
    setIsScanning,
    setScanAvgMsPerPdf,
    setScanElapsedMs,
    setScanPps,
    setScanProgress,
    setScanStartTs,
    setScanTotal,
  } = state;

  // Event listeners for scan progress
  useEffect(() => {
    // Listen for scan progress events
    const unlistenProgress = listen("scan_progress", (event) => {
      const payload = event.payload as ScanProgressPayload;

      if (payload.status === "debug") {
        // Sichtbarer Debug-Ausgabe für Skip-Flag pro Datei
        debugLog("debug", "investments", "🧪 scan_progress debug", {
          currentFile: payload.currentFile,
          file: "useScanProgress.ts",
          function: "useScanProgress",
          message: payload.message,
          module: "parqet",
          skip_enrichment: payload.skip_enrichment,
        });
      }

      interface ExtendedPayload {
        [key: string]: unknown;
        processed: number;
        total: number;
      }
      const extendedPayload = payload as unknown as ExtendedPayload;
      if (typeof extendedPayload.processed === "number") {
        setScanProgress(extendedPayload.processed);
      }
      if (typeof extendedPayload.total === "number") {
        setScanTotal(extendedPayload.total);
      }
      setCurrentScanFile(payload.currentFile);
      if (typeof payload.elapsed_ms === "number") {
        setScanElapsedMs(payload.elapsed_ms);
      }
      if (typeof payload.processed_per_sec === "number") {
        setScanPps(payload.processed_per_sec);
      }
      if (typeof payload.avg_ms_per_pdf === "number") {
        setScanAvgMsPerPdf(payload.avg_ms_per_pdf);
      }

      if (payload.status === "starting") {
        setIsScanning(true);
        setScanStartTs(Date.now());
      }
    });

    // Listen for scan completion
    const unlistenDone = listen("scan_progress_done", (event) => {
      const payload = event.payload as ScanDonePayload;

      setIsScanning(false);
      setScanStartTs(null);
      if (typeof payload.total_ms === "number") {
        setScanElapsedMs(payload.total_ms);
      }
      if (typeof payload.pdfs_per_sec === "number") {
        setScanPps(payload.pdfs_per_sec);
      }
      if (typeof payload.avg_ms_per_pdf === "number") {
        setScanAvgMsPerPdf(payload.avg_ms_per_pdf);
      }
      debugLog("info", "investments", "Scan completed", {
        avg_ms_per_pdf: payload.avg_ms_per_pdf,
        failed: payload.failed,
        module: "parqet",
        pdfs_per_sec: payload.pdfs_per_sec,
        successful: payload.successful,
        total: payload.total,
        total_ms: payload.total_ms,
      });
    });

    return () => {
      void unlistenProgress.then((f) => {
        f();
      });
      void unlistenDone.then((f) => {
        f();
      });
    };
  }, [
    setScanProgress,
    setScanTotal,
    setCurrentScanFile,
    setScanElapsedMs,
    setScanPps,
    setScanAvgMsPerPdf,
    setIsScanning,
    setScanStartTs,
  ]);

  // Lightweight UI-Timer bis erste Progress-Events mit elapsed kommen
  useEffect(() => {
    if (!isScanning || !scanStartTs) {
      return;
    }
    const id = setInterval(() => {
      setScanElapsedMs(Date.now() - scanStartTs);
    }, 250);
    return () => {
      clearInterval(id);
    };
  }, [isScanning, scanStartTs, setScanElapsedMs]);
};
